import { useEffect, useState } from 'react';
import { financialConfigApi } from '@verlo/shared/services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './Section';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Label } from '@verlo/shared/components/ui/label';
import { Switch } from '@verlo/shared/components/ui/switch';
import { Notice } from '@verlo/shared/components/ui/notice';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@verlo/shared/components/ui/select';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSite } from '../../../contexts/SiteContext';

/**
 * Settings › Company › Tills (modifiers plan, 21 Sep 2026): priced free text. Off by
 * default — the managers' call: open-price typed entries are the one part of the till
 * that could be used to under-ring. A manager switches it on for the business or for one
 * site; a till can be overridden by `VERLO_FREE_TEXT_PRICING` on the node. When it's on,
 * every priced entry is on the Typed extras report whatever the amount.
 */
const TillsCard = () => {
  const { sites } = useSite();
  const [cfg, setCfg] = useState(null);
  const [site, setSite] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { financialConfigApi.getSettings(undefined).then(({ data }) => setCfg(data)).catch(() => {}); }, []);
  if (!cfg) return null;

  const def = { enabled: false, cap: 5, step: 0.5, ...(cfg.default_free_text || {}) };
  const venue = ((cfg.venues || {})[site] || {}).free_text || {};
  const setDef = (patch) => setCfg({ ...cfg, default_free_text: { ...def, ...patch } });
  const setVenue = (patch) => setCfg({ ...cfg, venues: { ...(cfg.venues || {}), [site]: { ...((cfg.venues || {})[site] || {}), free_text: { ...venue, ...patch } } } });
  const num = (v) => (v === '' || v == null ? null : Number(v));

  const save = async () => {
    setSaving(true);
    try {
      const patch = { default_free_text: { enabled: !!def.enabled, cap: Number(def.cap) || 0, step: Number(def.step) || 0.5 } };
      if (site) patch.venues = { [site]: { free_text: { enabled: venue.enabled ?? null, cap: num(venue.cap), step: num(venue.step) } } };
      const { data } = await financialConfigApi.updateSettings(patch);
      setCfg(data);
      toast.success('Till settings saved');
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save the till settings"); }
    finally { setSaving(false); }
  };

  const effectiveOn = site && venue.enabled != null ? venue.enabled : def.enabled;

  return (
    <Card data-testid="settings-tills">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">Tills · typed extras</CardTitle>
        <CardDescription>Whether a server can put a price on something typed into a modifier sheet. A £0 note never needs this. Off unless a manager switches it on.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <label className="flex items-center justify-between gap-3 rounded-md border border-hairline px-3 py-2 text-[13.5px] text-ink">
          <span>Priced free text, for the business<span className="block text-[12px] text-ink-faint">Every priced entry goes on the <Link to="/back-office/reports/typed-extras" className="text-verlo-hover underline">Typed extras</Link> report, whatever the amount.</span></span>
          <Switch checked={!!def.enabled} onCheckedChange={(v) => setDef({ enabled: v })} data-testid="settings-freetext-enabled" />
        </label>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label className="text-[12.5px] text-ink-soft">No manager needed up to</Label>
            <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft">£</span>
              <Input type="number" inputMode="decimal" min="0" step="0.5" className="pl-7 font-mono tabular-nums" value={def.cap ?? ''} onChange={(e) => setDef({ cap: e.target.value })} data-testid="settings-freetext-cap" /></div>
            <p className="text-[12px] text-ink-faint">Over this a manager's PIN is asked for on the till.</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[12.5px] text-ink-soft">Price step</Label>
            <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft">£</span>
              <Input type="number" inputMode="decimal" min="0.01" step="0.1" className="pl-7 font-mono tabular-nums" value={def.step ?? ''} onChange={(e) => setDef({ step: e.target.value })} data-testid="settings-freetext-step" /></div>
            <p className="text-[12px] text-ink-faint">What the − and + move by. 50p by default.</p>
          </div>
        </div>

        {sites.length > 0 && (
          <div className="space-y-3 border-t border-hairline pt-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-[13px] font-semibold text-ink">One site's own setting</p>
              <Select value={site || 'none'} onValueChange={(v) => setSite(v === 'none' ? '' : v)}>
                <SelectTrigger className="h-9 w-[220px] bg-white" data-testid="settings-freetext-site"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No site override</SelectItem>
                  {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {site && (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="space-y-1">
                  <Label className="text-[12px] text-ink-soft">Priced free text here</Label>
                  <Select value={venue.enabled == null ? 'inherit' : venue.enabled ? 'on' : 'off'} onValueChange={(v) => setVenue({ enabled: v === 'inherit' ? null : v === 'on' })}>
                    <SelectTrigger className="h-9 bg-white" data-testid="settings-freetext-venue-enabled"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="inherit">As the business</SelectItem><SelectItem value="on">On</SelectItem><SelectItem value="off">Off</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="space-y-1"><Label className="text-[12px] text-ink-soft">Cap</Label><Input type="number" inputMode="decimal" className="font-mono tabular-nums" placeholder={String(def.cap)} value={venue.cap ?? ''} onChange={(e) => setVenue({ cap: e.target.value })} /></div>
                <div className="space-y-1"><Label className="text-[12px] text-ink-soft">Step</Label><Input type="number" inputMode="decimal" className="font-mono tabular-nums" placeholder={String(def.step)} value={venue.step ?? ''} onChange={(e) => setVenue({ step: e.target.value })} /></div>
                <p className="col-span-full text-[12px] text-ink-faint">Blank uses the business setting. A single till can also be forced on or off with <code className="font-mono">VERLO_FREE_TEXT_PRICING</code> on Verlo Node.</p>
              </div>
            )}
          </div>
        )}

        {effectiveOn && (
          <Notice tone="warn" word="On" title="Priced free text is on" data-testid="settings-freetext-on-notice">
            Servers can price typed entries up to the cap without a PIN. Read the Typed extras report at the end of every day.
          </Notice>
        )}
        <div className="flex justify-end">
          <Button className="bg-verlo hover:bg-verlo-hover" onClick={save} disabled={saving} data-testid="settings-freetext-save">{saving && <Loader2 size={14} className="mr-2 animate-spin" />}Save</Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default TillsCard;
